import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const languages = [
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
  { code: 'de', label: 'DE' },
  { code: 'da', label: 'DA' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      if (mobileOpen) setMobileOpen(false);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [mobileOpen]);

  const handleLangChange = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  const currentLang = i18n.language || 'en';
  const basePath = currentLang === 'en' ? '' : `/${currentLang}`;

  const navLinks = [
    { label: t('nav.home'), path: `${basePath}/`, type: 'route' },
    { label: t('nav.categories'), path: `${basePath}/#categories`, type: 'section' },
    { label: t('nav.shoppingGuide'), path: `${basePath}/article/buying-guide`, type: 'route' },
    { label: t('nav.guides'), path: `${basePath}/#articles`, type: 'section' },
    { label: t('nav.reviews'), path: `${basePath}/#reviews`, type: 'section' },
    { label: t('nav.safety'), path: `${basePath}/#safety`, type: 'section' },
  ];

  const handleNav = (path: string, type: string) => {
    setMobileOpen(false);
    setDropdownOpen(false);
    if (type === 'section') {
      const id = path.split('#')[1];
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate(`${basePath}/`);
        setTimeout(() => {
          document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
        }, 150);
      }
    } else {
      navigate(path);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
      <div className="w-full px-4 sm:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <a
            href={`${basePath}/`}
            className="flex items-center gap-2 font-bold text-lg tracking-tight"
            onClick={(e) => { e.preventDefault(); navigate(`${basePath}/`); window.scrollTo({ top: 0 }); }}
          >
            <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center text-white text-sm font-bold">AC</div>
            <span className="font-semibold text-gray-900">AllChinaBuy</span>
          </a>

          <div className="hidden lg:flex items-center gap-5">
            {navLinks.map((link) => (
              <button
                key={link.path}
                onClick={() => handleNav(link.path, link.type)}
                className="text-sm font-semibold text-gray-800 hover:text-emerald-600 transition-colors whitespace-nowrap"
              >
                {link.label}
              </button>
            ))}

            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1 text-sm font-semibold text-gray-800 hover:text-emerald-600 transition-colors whitespace-nowrap"
              >
                {t('dropdown.title')}
                <i className={`ri-arrow-down-s-line transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-64 bg-white rounded-xl shadow-lg border border-gray-100 py-2 max-h-96 overflow-y-auto grid grid-cols-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <i className={`${cat.icon} text-emerald-600`}></i>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1 text-xs font-bold px-2.5 py-1.5 rounded-md text-gray-700 hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                {currentLang.toUpperCase()}
                <i className="ri-arrow-down-s-line"></i>
              </button>
              {langOpen && (
                <div className="absolute top-full mt-1 right-0 bg-white rounded-lg shadow-lg border border-gray-100 py-1 min-w-[80px]">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLangChange(lang.code)}
                      className={`block w-full text-left px-3 py-1.5 text-xs font-semibold ${currentLang === lang.code ? 'text-emerald-600 bg-emerald-50' : 'text-gray-700 hover:bg-gray-50'}`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors whitespace-nowrap"
            >
              {t('hero.cta.secondary')}
              <i className="ri-external-link-line text-xs"></i>
            </a>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 rounded-lg text-gray-800 hover:bg-gray-100 transition-colors"
              aria-label="Menu"
            >
              <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 px-4 py-4 shadow-lg max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <button
                key={link.path}
                onClick={() => handleNav(link.path, link.type)}
                className="text-left px-3 py-2.5 text-sm font-semibold text-gray-800 hover:bg-emerald-50 hover:text-emerald-700 rounded-lg transition-colors"
              >
                {link.label}
              </button>
            ))}

            <div className="border-t border-gray-100 pt-3 mt-2">
              <p className="text-xs font-bold text-gray-500 px-3 mb-2 uppercase tracking-wide">{t('dropdown.title')}</p>
              <div className="grid grid-cols-2 gap-1.5">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
                  >
                    <i className={`${cat.icon} text-emerald-600`}></i>
                    <span className="leading-tight">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="mt-3 flex items-center justify-center gap-2 text-sm font-semibold px-4 py-3 rounded-lg bg-emerald-600 text-white"
            >
              {t('hero.cta.secondary')}
              <i className="ri-external-link-line"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}